/* eslint-disable padding-line-between-statements */
import { useState, useEffect, useRef } from 'react';
import { Modal } from '~/components/ui/Modal';
import { setAllSupabaseData, updateSupabaseConnectionStatus } from '~/lib/stores/integrations';
import { projectId } from '~/lib/persistence';
import { toast } from 'react-toastify';
import { useTranslation } from '~/lib/hooks/useTranslation';
import { usePosthogCommonEvents } from '~/lib/hooks/posthog/usePosthogCommonEvents';
import { SupabaseIcon } from './SupabaseIcon';
import axios from 'axios';
import * as Sentry from '@sentry/remix';
import { useFetcher } from '@remix-run/react';

interface Organization {
  id: string;
  name: string;
}

interface Project {
  id: string;
  name: string;
  status: string;
  created_at?: string;
}

interface ProjectCredentials {
  anon_key: string;
}

export interface TokenResponse {
  access_token: string;
  token_type: string;
  expires_in: number;
  refresh_token: string;
}

interface SupabaseApiError {
  error: string;
}

interface SupabaseOauthModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (credentials: {
    projectId: string;
    projectName: string;
    organizationId: string;
    organizationName: string;
    anonKey: string;
  }) => void;
  onError: (error: { message: string }) => void;
  currentProjectId?: string;
  currentProjectName?: string;
  currentOrganizationId?: string;
  currentOrganizationName?: string;
  setShowSupabaseOAuthModal?: (isOpen: boolean) => void;
  initialStep?: 'storage-choice';
  onLocalChoice?: () => void;
}

export function SupabaseOauthModal({
  isOpen,
  onClose,
  onSuccess,
  onError,
  currentProjectId,
  currentProjectName,
  currentOrganizationId,
  currentOrganizationName,
  setShowSupabaseOAuthModal,
  initialStep,
  onLocalChoice,
}: SupabaseOauthModalProps) {
  const { t } = useTranslation();
  const fetcher = useFetcher<{ tokens: Record<string, any> }>();
  const { trackSupabaseEvent } = usePosthogCommonEvents();
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState<'storage-choice' | 'auth' | 'organizations' | 'projects' | 'credentials'>('auth');
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [selectedOrganization, setSelectedOrganization] = useState<Organization | null>(null);
  const [projects, setProjects] = useState<Project[]>([]);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [accessToken, setAccessToken] = useState<string>('');
  const [isDisconnecting, setIsDisconnecting] = useState(false);

  // console.log('isOpen >>>', isOpen);

  // Add timeout management
  const pollIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // PostHog: track modal open for PM funnel
  useEffect(() => {
    if (isOpen) {
      const hasExistingConnection = !!localStorage.getItem('supabase_access_token');
      const viewOnlyMode = !!(projectId.get() && currentProjectId);
      trackSupabaseEvent('modal_open', {
        initial_step: initialStep === 'storage-choice' ? 'storage-choice' : 'auth',
        has_existing_project: !!currentProjectId,
        has_existing_connection: hasExistingConnection,
        view_only_mode: viewOnlyMode,
      });
    }
  }, [isOpen]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }

      window.removeEventListener('message', handleMessage);
    };
  }, []);

  // Function to stop loading and clear timeout
  const stopLoading = () => {
    setLoading(false);
  };

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    if (fetcher.state === 'idle' && !fetcher.data) {
      const accessToken = localStorage.getItem('supabase_access_token') || '';
      const refreshToken = localStorage.getItem('supabase_refresh_token') || '';

      fetcher.submit(
        { provider: 'supabase', localAccessToken: accessToken, localRefreshToken: refreshToken },
        { method: 'POST', action: '/api/access-token', encType: 'application/json' },
      );
    }
  }, [isOpen, fetcher]);

  // Check for existing token when modal opens
  useEffect(() => {
    if (!isOpen) {
      return;
    }
    if (isOpen && initialStep === 'storage-choice') {
      setStep('storage-choice');
      return;
    }

    if (fetcher.data?.tokens) {
      const { supabase } = fetcher.data.tokens;
      console.log('[int] fetching token for supabase: ', supabase);

      if (supabase) {
        localStorage.setItem('supabase_access_token', supabase.accessToken);
        localStorage.setItem('supabase_refresh_token', supabase.refreshToken);
      }
    }

    const checkToken = async () => {
      console.log('refresh token useEffect is called each time the modal is mounted 2');

      const storedToken = localStorage.getItem('supabase_access_token');

      if (storedToken) {
        // Validate token before using it
        const result = await validateAndUseToken(storedToken);
        console.log('[int] result >>>', result);
      } else {
        setStep('auth');

        // log out should only happen in the case of invalid token, this call gets fired even when the token is not present in local storage

        // await handleLogout();
      }

      setLoading(false);
    };
    checkToken();
  }, [isOpen, fetcher.data]);

  // Function to refresh the token
  const refreshToken = async (token: string): Promise<string> => {
    try {
      // console.log('refreshing token', token, 'step', step);

      const response = await fetch('/api/supabase/refresh-token', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to refresh token');
      }

      const data = (await response.json()) as TokenResponse;
      const newToken = data.access_token;
      const newRefreshToken = data.refresh_token;

      // console.log('newToken refreshed', newToken, newRefreshToken, data);

      if (newToken && newRefreshToken) {
        setAccessToken(newToken);
        localStorage.setItem('supabase_access_token', newToken);
        localStorage.setItem('supabase_refresh_token', newRefreshToken);
      } else if (newToken && !newRefreshToken) {
        setAccessToken(newToken);
        localStorage.setItem('supabase_access_token', newToken);
        localStorage.removeItem('supabase_refresh_token');
      }

      return newToken;
    } catch (error) {
      console.error('[Supabase Auth] Token refresh failed:', error);
      throw error;
    }
  };

  // console.log('refresh token useEffect is called each time the modal is mounted');

  // // Set up automatic token refresh
  // useEffect(() => {
  //   // Refresh token every 45 minutes to ensure it never expires
  //   const refreshInterval = setInterval(
  //     async () => {
  //       try {
  //         const token = localStorage.getItem('supabase_refresh_token');

  //         if (!token) {
  //           return;
  //         }

  //         const newToken = await refreshToken(token);
  //         setAccessToken(newToken);
  //       } catch (error) {
  //         console.error('[Supabase Auth] Auto-refresh failed:', error);

  //         // If refresh fails, we'll need to re-authenticate
  //         handleLogout();
  //       }
  //     },
  //     15 * 60 * 1000, // 15 minutes
  //   );

  //   return () => clearInterval(refreshInterval);
  // }, []);

  const validateAndUseToken = async (token: string) => {
    try {
      console.log('[Supabase Auth] Starting token validation...');
      setLoading(true);

      // Try to fetch organizations to validate token
      const response = await fetch('/api/supabase/organizations?path=organizations', {
        headers: {
          Authorization: token,
          'Content-Type': 'application/json',
        },
      });

      // console.log('[Supabase Auth] Validation response status:', response.status);

      if (!response.ok) {
        if (response.status === 401) {
          console.error('[Supabase Auth] Token validation failed with 401');
          handleLogout();
          throw new Error('Authentication expired. Please login again.');
        }

        const errorData = (await response.json()) as SupabaseApiError;
        console.error('[Supabase Auth] Token validation failed:', errorData);
        throw new Error(errorData.error || 'Failed to validate token');
      }

      const orgs = (await response.json()) as Organization[];
      console.log('[Supabase Auth] Organizations fetched:', orgs);

      if (!orgs || orgs.length === 0) {
        throw new Error('No organizations found');
      }

      // Token is valid, set it and update state
      console.log('[Supabase Auth] Token validated successfully');
      trackSupabaseEvent('auth_succeeded', { organizations_count: orgs.length });
      setAccessToken(token);
      setOrganizations(orgs);

      // Try to find the current organization or use the first one
      const currentOrg = currentOrganizationId ? orgs.find((org) => org.id === currentOrganizationId) : orgs[0];
      console.log('currentOrg data >>>', currentOrg);

      if (currentOrg) {
        setSelectedOrganization(currentOrg);
        setStep('projects');
        await fetchProjects(currentOrg.id);
      } else {
        // No valid organization found; request parent to show OAuth modal
        if (setShowSupabaseOAuthModal) {
          setShowSupabaseOAuthModal(true);
        }

        throw new Error('No valid organization found');
      }
    } catch (error) {
      console.error('[Supabase Auth] Token validation error:', error);

      const errorMessage = error instanceof Error ? error.message : '';
      trackSupabaseEvent('auth_failed', {
        reason:
          errorMessage.includes('Authentication expired') || errorMessage.includes('401') ? 'invalid_token' : 'other',
      });
      handleLogout();
      throw error; // Propagate error for proper handling
    }
  };

  const handleMessage = async (event: MessageEvent) => {
    console.log('[Supabase Auth] Received message:', event.data);

    if (event.origin !== window.location.origin) {
      console.log('[Supabase Auth] Message from different origin:', event.origin);
      return;
    }

    if (event.data?.type === 'SUPABASE_AUTH_SUCCESS') {
      try {
        console.log('[Supabase Auth] Processing auth success message', event.data);

        const { token, refresh_token: refreshTokenFromMessage } = event.data as {
          token?: string;
          refresh_token?: string | null;
        };

        if (!token) {
          console.error('[Supabase Auth] No token in success message');
          throw new Error('No token received');
        }

        console.log('[int] Storing token and validating...');
        localStorage.setItem('supabase_access_token', token);

        if (refreshTokenFromMessage) {
          localStorage.setItem('supabase_refresh_token', refreshTokenFromMessage);
        }

        // Then validate and use it
        await validateAndUseToken(token);

        // Only remove listener after successful validation
        window.removeEventListener('message', handleMessage);
        console.log('[Supabase Auth] Auth flow completed successfully');
      } catch (error) {
        console.error('[Supabase Auth] Error in auth success flow:', error);
        trackSupabaseEvent('auth_failed', { reason: 'invalid_token' });
        localStorage.removeItem('supabase_access_token');
        localStorage.removeItem('supabase_refresh_token');
        setAccessToken('');
        setStep('auth');
        stopLoading();
        onError({ message: error instanceof Error ? error.message : 'Failed to authenticate' });
      }
    } else if (event.data?.type === 'SUPABASE_AUTH_ERROR') {
      console.error('[Supabase Auth] Received auth error:', event.data.error);
      trackSupabaseEvent('auth_failed', { reason: 'auth_error' });
      window.removeEventListener('message', handleMessage);
      stopLoading();
      onError({ message: event.data.error || 'Authentication failed' });
    }
  };

  // Update handleAuth to properly clean up on error
  const handleAuth = async () => {
    try {
      trackSupabaseEvent('auth_started', {
        has_existing_project: !!currentProjectId,
        entry_step: step,
      });
      console.log('[Supabase Auth] Starting auth flow...');
      localStorage.removeItem('supabase_access_token');
      localStorage.removeItem('supabase_refresh_token');
      setAccessToken('');
      setStep('auth');
      setLoading(true);

      // Add message listener before opening popup
      window.addEventListener('message', handleMessage);
      console.log('[Supabase Auth] Added message listener');

      // Open popup window
      const width = 600;
      const height = 700;
      const left = window.screenX + (window.outerWidth - width) / 2;
      const top = window.screenY + (window.outerHeight - height) / 2;

      console.log('[Supabase Auth] Opening auth popup...');

      const popup = window.open(
        '/auth/supabase',
        'Supabase Login',
        `width=${width},height=${height},left=${left},top=${top}`,
      );

      if (!popup) {
        trackSupabaseEvent('auth_failed', { reason: 'popup_blocked' });
        throw new Error('Failed to open popup window. Please allow popups for this site.');
      }

      // Focus the popup
      popup.focus();

      let isAuthenticated = false;
      let hasHandledClosure = false;
      let pollAttempts = 0;
      const maxPollAttempts = 35;

      // Poll localStorage every second to check for token
      pollIntervalRef.current = setInterval(() => {
        pollAttempts++;

        const token = localStorage.getItem('supabase_access_token');
        console.log('[Supabase Auth] Polling attempt', pollAttempts, 'token exists:', !!token);

        if (token) {
          console.log('[Supabase Auth] Token found in localStorage, stopping polling');
          clearInterval(pollIntervalRef.current!);
          pollIntervalRef.current = null;
          isAuthenticated = true;
          hasHandledClosure = true;
          stopLoading();
          validateAndUseToken(token);
        }

        if (pollAttempts >= maxPollAttempts && !isAuthenticated) {
          console.log('[Supabase Auth] Max poll attempts reached');
          trackSupabaseEvent('auth_failed', { reason: 'timeout' });
          clearInterval(pollIntervalRef.current!);
          pollIntervalRef.current = null;
          hasHandledClosure = true;
          stopLoading();
          window.removeEventListener('message', handleMessage);
          onError({ message: 'Authentication timeout. Please try again.' });
        }
      }, 1000);

      // Set a timeout to cleanup
      setTimeout(
        () => {
          if (!isAuthenticated && !hasHandledClosure && pollIntervalRef.current) {
            console.log('[Supabase Auth] Final timeout cleanup');
            trackSupabaseEvent('auth_failed', { reason: 'timeout' });
            hasHandledClosure = true;
            clearInterval(pollIntervalRef.current!);
            pollIntervalRef.current = null;
            stopLoading();
            window.removeEventListener('message', handleMessage);
            onError({ message: 'Authentication timeout. Please try again.' });
          }
        },
        maxPollAttempts * 1000 + 1000,
      );
    } catch (error) {
      console.error('[Supabase Auth] Error in auth flow:', error);
      const message = error instanceof Error ? error.message : 'An unknown error occurred';
      trackSupabaseEvent('auth_failed', {
        reason: message.includes('popup') ? 'popup_blocked' : 'other',
      });
      stopLoading();
      window.removeEventListener('message', handleMessage);
      onError({ message: error instanceof Error ? error.message : 'An unknown error occurred' });
    }
  };

  const fetchProjects = async (orgId: string) => {
    try {
      setLoading(true);
      console.log('[Supabase Auth] Fetching projects for org:', orgId);

      const supabaseToken = localStorage.getItem('supabase_access_token');

      if (!supabaseToken) {
        await handleLogout();
        throw new Error('No access token found');
      } else if (!orgId || orgId === '') {
        throw new Error('No organization ID found');
      }

      // Use the same token format as in validateAndUseToken
      const response = await fetch(`/api/supabase/organizations/?path=organizations&organization_id=${orgId}`, {
        headers: {
          Authorization: supabaseToken,
          'Content-Type': 'application/json',
        },
      });

      // console.log('[Supabase Auth] Projects response:', response);

      if (!response.ok) {
        if (response.status === 401) {
          console.error('[Supabase Auth] Project fetch failed with 401');
          localStorage.removeItem('supabase_access_token');
          localStorage.removeItem('supabase_refresh_token');
          setStep('auth');
          throw new Error('Authentication expired. Please login again.');
        }

        const errorData = (await response.json()) as SupabaseApiError;
        console.error('[Supabase Auth] Project fetch failed:', errorData);
        throw new Error(errorData.error || 'Failed to fetch projects');
      }

      const projectsData = (await response.json()) as Project[];
      console.log('[Supabase Auth] Projects data:', projectsData);
      setProjects(projectsData);
      trackSupabaseEvent('projects_loaded', {
        projects_count: projectsData.length,
        organization_id: orgId,
      });
      stopLoading();
    } catch (error) {
      console.error('[Supabase Auth] Error fetching projects:', error);
      stopLoading();
      onError({ message: error instanceof Error ? error.message : 'Failed to fetch projects' });
    }
  };

  const handleCreateProject = (source: 'header' | 'empty_state') => {
    trackSupabaseEvent('create_project_clicked', { source });
    window.open(`https://supabase.com/dashboard/new/${selectedOrganization?.id}`, '_blank');
  };

  const handleProjectSelection = async (project: Project) => {
    trackSupabaseEvent('project_selected', {
      project_id: project.id,
      project_status: project.status,
      is_current_project: project.id === currentProjectId,
    });
    setSelectedProject(project);
  };

  const handleProjectSelect = async (project: Project) => {
    try {
      setLoading(true);

      console.log('Fetching credentials for project:', project.id);

      const response = await fetch(`/api/supabase/organizations?path=projects/${project.id}/api-keys`, {
        headers: {
          Authorization: accessToken,
          'Content-Type': 'application/json',
        },
      });

      // console.log('Credentials response:', response);

      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem('supabase_access_token');
          localStorage.removeItem('supabase_refresh_token');
          setStep('auth');
          throw new Error('Authentication expired. Please login again.');
        }

        const errorData = (await response.json()) as SupabaseApiError;
        console.log('errorData', errorData);

        throw new Error(errorData.error || 'Failed to fetch project credentials');
      }

      const credentials = (await response.json()) as ProjectCredentials;

      // console.log('Credentials received successfully', credentials);

      setAllSupabaseData(
        project.id,
        project.name,
        credentials.anon_key,
        selectedOrganization?.id || '',
        selectedOrganization?.name || '',
      );
      updateSupabaseConnectionStatus(true, false, accessToken);

      trackSupabaseEvent('project_connected', {
        project_id: project.id,
        organization_id: selectedOrganization?.id || '',
        is_reconnect: !!currentProjectId,
      });

      // Configure Supabase project with redirect URLs after successful connection
      try {
        console.log('[Supabase Modal] Configuring project redirect URLs...');

        const configResponse = await fetch('/api/supabase/config', {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            projectId: project.id,
          }),
        });

        if (configResponse.ok) {
          const configResult = await configResponse.json();

          // console.log('[Supabase Modal] Project configured successfully:', configResult);
        } else {
          const configError = await configResponse.json();
          console.warn('[Supabase Modal] Failed to auto-configure redirect URLs:', configError);

          // Don't fail the entire flow, just log the warning
        }
      } catch (configError) {
        console.warn('[Supabase Modal] Error configuring redirect URLs:', configError);

        // Don't fail the entire flow, just log the warning
      }

      onSuccess({
        projectId: project.id,
        projectName: project.name,
        organizationId: selectedOrganization?.id || '',
        organizationName: selectedOrganization?.name || '',
        anonKey: credentials.anon_key,
      });

      trackSupabaseEvent('modal_closed', {
        step: 'credentials',
        loading: false,
        reason: 'success',
      });
      onClose();
    } catch (error) {
      console.error('Error fetching project credentials:', error);
      const errorMessage = error instanceof Error ? error.message : '';
      const reason =
        errorMessage.includes('Authentication expired') || errorMessage.includes('401')
          ? '401'
          : errorMessage
            ? 'api_error'
            : 'unknown';
      trackSupabaseEvent('project_connect_failed', {
        project_id: project.id,
        reason,
        has_access_token: !!accessToken,
      });
      stopLoading(); // Error - stop loading
      onError({ message: error instanceof Error ? error.message : 'Failed to fetch project credentials' });
    }
  };

  const handleMakeProjectActive = async (project: Project) => {
    // console.log('handleMakeProjectActive: ', project);
    window.open(`https://supabase.com/dashboard/project/${project.id}`, '_blank');
  };

  const handleLogout = async () => {
    try {
      setIsDisconnecting(true);

      await axios.delete('/api/user-integration', {
        data: {
          provider: 'supabase',
        },
      });

      localStorage.removeItem('supabase_access_token');
      localStorage.removeItem('supabase_refresh_token');
      setAccessToken('');
      setOrganizations([]);
      setSelectedOrganization(null);
      setProjects([]);
      setSelectedProject(null);
      setStep('auth');
    } catch (error) {
      console.error('Error deleting user integration from SupabaseOAuthModal:', error);
      Sentry.captureException(error);

      toast.error('Failed to disconnect Supabase. Please try again or refresh the page.');
    } finally {
      setIsDisconnecting(false);
    }
  };

  const handleCloudChoice = async () => {
    trackSupabaseEvent('storage_choice', {
      choice: 'cloud',
      trigger: initialStep === 'storage-choice' ? 'project_creation' : 'settings',
    });
    const storedToken = localStorage.getItem('supabase_access_token');
    const shouldAutoTriggerAuth = initialStep === 'storage-choice';

    if (storedToken) {
      setLoading(true);

      try {
        await validateAndUseToken(storedToken);
      } catch {
        setStep('auth');
        setLoading(false);

        if (shouldAutoTriggerAuth) {
          setTimeout(() => handleAuth(), 0);
        }
      }
    } else {
      await handleLogout();

      if (shouldAutoTriggerAuth) {
        setTimeout(() => handleAuth(), 0);
      }
    }
  };

  const handleLocalChoice = () => {
    trackSupabaseEvent('storage_choice', {
      choice: 'local',
      trigger: initialStep === 'storage-choice' ? 'project_creation' : 'settings',
    });
    onLocalChoice?.();
    onClose();
  };

  const handleClose = () => {
    // Clear the polling interval
    console.log('[Supabase Auth] Closing modal');

    const closeReason = loading ? 'auth_error' : 'user_close';
    trackSupabaseEvent('modal_closed', {
      step,
      loading,
      reason: closeReason,
    });

    if (!loading) {
      onClose();
      return;
    }

    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }

    console.log('[Supabase Auth] Cleared polling interval');

    // toast.error('Authentication failed. Please try again.');
    // console.log('[Supabase Auth] Set error');

    setLoading(false);
    console.log('[Supabase Auth] Set loading to false');

    setStep('auth');
    console.log('[Supabase Auth] Set step to auth');

    // Clear any message listeners
    window.removeEventListener('message', handleMessage);
    console.log('[Supabase Auth] Cleared message listeners');

    // Call the original onClose
    onClose();
    console.log('[Supabase Auth] Called onClose');
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) {
      return '12/05/25';
    }

    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: '2-digit' });
    } catch {
      return '12/05/25';
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose}>
      <div className="w-full max-w-[580px] max-h-[600px] overflow-y-auto">
        <div className="bg-dualite-background-tertiary rounded-lg border border-dualite-border-primary shadow-2xl relative w-full overflow-hidden">
          {/* Header */}
          <div className="flex flex-col p-4 border-b border-dualite-border-primary">
            <div className="flex justify-between items-start">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <SupabaseIcon size={20} withBackground />
                  <span className="text-dualite-text-modal-primary font-semibold">
                    {step === 'storage-choice'
                      ? t('modals.supabase.storageChoiceHeading', 'FullStack App Configuration')
                      : accessToken
                        ? t('modals.supabase.connectedTitle')
                        : t('modals.supabase.title')}
                  </span>
                  {step !== 'storage-choice' && (
                    <button
                      className="flex items-center gap-1 text-blue-400 hover:text-blue-300 transition-colors px-2 py-1 mt-1 rounded-full bg-white/10 hover:bg-white/20 cursor-pointer"
                      onClick={() => {
                        trackSupabaseEvent('docs_clicked');
                        window.open('https://alphadocs.dualite.dev/integrations/supabase', '_blank');
                      }}
                    >
                      <span className="text-xs text-dualite-text-modal-secondary">{t('modals.supabase.docs')}</span>
                      <div className="i-ph:link w-3 h-3" />
                    </button>
                  )}
                </div>
              </div>
              <button
                onClick={handleClose}
                className="p-1 text-dualite-text-modal-primary bg-transparent border-none rounded-md hover:bg-white/10 transition-colors"
              >
                <div className="i-ph:x w-4 h-4" />
              </button>
            </div>
            {!!projectId.get() && currentProjectId && step !== 'auth' && (
              <div className="mt-2 flex items-center gap-2 text-xs text-dualite-text-modal-secondary bg-dualite-background-tertiary p-2 rounded-md">
                <div className="i-ph:info w-4 h-4" />
                <span>{t('modals.supabase.viewOnlyMode')}</span>
              </div>
            )}
            {currentProjectId && currentProjectName && currentOrganizationName && step === 'auth' && (
              <div className="mt-2 flex flex-col gap-2 text-xs text-dualite-text-modal-secondary bg-blue-500/10 border border-blue-500/20 p-3 rounded-md">
                <div className="flex items-center gap-2">
                  <div className="i-ph:check-circle w-4 h-4 text-blue-400" />
                  <span className="font-medium">{t('modals.supabase.reconnectMessage')}</span>
                </div>
                <div className="ml-6">
                  <p className="mb-1">{t('modals.supabase.reconnectMessage')}</p>

                  <p className="flex items-center gap-2">
                    <strong>{t('modals.supabase.organization')}</strong>
                    <span className="bg-white/20 px-2 py-1 rounded text-dualite-text-modal-primary font-medium">
                      {currentOrganizationName}
                    </span>
                  </p>
                  <p className="flex items-center gap-2">
                    <strong>{t('modals.supabase.project')}</strong>
                    <span className="bg-white/20 px-2 py-1 rounded text-dualite-text-modal-primary font-medium">
                      {currentProjectName}
                    </span>
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Content */}
          <div className="p-6">
            {step === 'storage-choice' && (
              <div className="space-y-6">
                <div className="text-center mb-2">
                  <div className="i-ph:cloud-arrow-up w-8 h-8 mx-auto mb-4 text-dualite-text-modal-primary" />
                  <h3 className="text-dualite-text-modal-primary text-base font-semibold mb-1">
                    {t('modals.supabase.storageChoiceTitle', 'Enable Cloud?')}
                  </h3>
                  <p className="text-dualite-text-modal-secondary text-xs mb-4 mt-2">
                    {t('modals.supabase.storageChoiceSubtitle', 'Get a full backend for your app — out of the box.')}
                  </p>
                  <div className="flex items-center justify-center gap-2 mb-3 flex-wrap">
                    <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/5 border border-dualite-border-primary text-dualite-text-modal-secondary text-xs">
                      <div className="i-ph:database w-3.5 h-3.5 text-[#3ECF8E]" />
                      <span>Database</span>
                    </div>
                    <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/5 border border-dualite-border-primary text-dualite-text-modal-secondary text-xs">
                      <div className="i-ph:folder-open w-3.5 h-3.5 text-[#3ECF8E]" />
                      <span>Storage</span>
                    </div>
                    <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/5 border border-dualite-border-primary text-dualite-text-modal-secondary text-xs">
                      <div className="i-ph:user-circle w-3.5 h-3.5 text-[#3ECF8E]" />
                      <span>Auth</span>
                    </div>
                  </div>
                  <p className="text-dualite-text-modal-secondary/60 text-xs mb-1">
                    {t('modals.supabase.storageChoicePricing', 'Free to start · pay only when you scale')}
                  </p>
                </div>
                <div className="flex flex-row-reverse gap-3 mx-4">
                  <button
                    onClick={handleCloudChoice}
                    className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-dualite-background-cta hover:bg-dualite-background-cta-hover text-dualite-text-cta rounded-lg transition-all text-sm font-medium cursor-pointer"
                  >
                    <span>{t('modals.supabase.connectToCloud', 'Yes, Proceed')}</span>
                  </button>
                  <button
                    onClick={handleLocalChoice}
                    className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-dualite-background-button hover:bg-dualite-background-button-hover text-dualite-text-modal-primary border border-dualite-border-primary rounded-lg transition-all text-sm font-medium cursor-pointer"
                  >
                    <span>{t('modals.supabase.doItLater', "I'll do it later")}</span>
                  </button>
                </div>

                <span className="text-dualite-text-modal-secondary text-xs text-center flex items-center justify-center gap-1 flex-wrap">
                  You can always enable Cloud later from the{' '}
                  <SupabaseIcon size={12} className="inline-block shrink-0" /> icon on the top right.
                </span>
              </div>
            )}

            {step === 'auth' && (
              <>
                {loading ? (
                  <div className="flex flex-col items-center justify-center py-12">
                    <div className="i-svg-spinners:90-ring-with-bg w-8 h-8 text-dualite-text-modal-secondary mb-4" />
                    <p className="text-sm text-dualite-text-modal-secondary">
                      {t('modals.supabase.checkingConnection')}
                    </p>
                  </div>
                ) : (
                  <div className="space-y-6">
                    <div className="text-center">
                      <SupabaseIcon size={96} withBackground className="mx-auto mb-6" />
                      <h3 className="text-dualite-text-modal-primary text-sm font-semibold mb-1">
                        {currentProjectId && currentProjectName && currentOrganizationName
                          ? t('modals.supabase.tocontinue')
                          : t('modals.supabase.linkAccount')}
                      </h3>
                      <p className="text-dualite-text-modal-secondary text-xs">
                        {t('modals.supabase.openSecureWindow')}
                      </p>
                    </div>

                    <div className="flex justify-center">
                      <button
                        onClick={handleAuth}
                        className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-dualite-background-cta hover:bg-dualite-background-cta-hover text-dualite-text-cta rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
                      >
                        <span>{t('modals.supabase.signInWithSupabase')}</span>
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}

            {step === 'projects' && (
              <div className="space-y-6">
                {/* Header with Organization Name and Action Buttons */}
                <div className="flex items-center justify-between mb-6">
                  <div className="flex flex-col">
                    <div className="flex items-center gap-2">
                      <h2 className="text-dualite-text-modal-primary text-[20px] font-semibold">
                        {selectedOrganization?.name || organizations[0]?.name || 'Your Organization'}
                      </h2>
                      <div className="flex items-center gap-2 mb-1">
                        <button
                          className="flex items-center gap-1 text-blue-400 hover:text-blue-300 transition-colors  px-2 py-1 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                          disabled={loading || isDisconnecting || (!!projectId.get() && !!currentProjectId)}
                          onClick={handleLogout}
                        >
                          <span className="text-xs text-dualite-text-modal-secondary pt-2">
                            {t('modals.supabase.switch')}
                          </span>
                          {isDisconnecting ? (
                            <div className="i-svg-spinners:90-ring-with-bg w-3 h-3 mt-2" />
                          ) : (
                            <div className="i-ph:arrows-left-right w-3 h-3 mt-2" />
                          )}
                        </button>
                      </div>
                    </div>
                    <p className="text-dualite-text-modal-tertiary text-sm mt-1">
                      {t('modals.supabase.organizationLabel')}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => {
                        const orgId = selectedOrganization?.id || organizations[0]?.id;

                        if (orgId) {
                          fetchProjects(orgId);
                        }
                      }}
                      disabled={loading || isDisconnecting || (!!projectId.get() && !!currentProjectId)}
                      className="flex items-center gap-2 px-3 py-2 text-sm bg-dualite-background-tertiary border border-white/10 text-dualite-text-modal-primary rounded-lg hover:bg-dualite-background-tertiary-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <div className="i-ph:arrows-clockwise w-4 h-4" />
                      <span>{t('modals.supabase.sync')}</span>
                    </button>
                    {!projectId.get() && (
                      <button
                        onClick={() => handleCreateProject('header')}
                        className="flex items-center gap-2 px-3 py-2 text-sm bg-dualite-background-cta text-dualite-text-cta rounded-lg hover:bg-dualite-background-cta-hover transition-colors"
                      >
                        <div className="i-ph:plus w-4 h-4" />
                        <span>{t('modals.supabase.createProject')}</span>
                      </button>
                    )}
                  </div>
                </div>

                {/* Projects Content */}
                {loading ? (
                  <div className="flex flex-col items-center justify-center w-full h-[150px] pb-20">
                    <div className="i-svg-spinners:90-ring-with-bg w-6 h-6 mb-2 text-dualite-text-modal-secondary bg-white/10 rounded-full p-2" />
                    <span className="text-dualite-text-modal-secondary text-sm">
                      {t('modals.supabase.loadingProjects')}
                    </span>
                  </div>
                ) : projects.length > 0 ? (
                  <div>
                    <div className="grid grid-cols-2 gap-4 pb-4">
                      {projects.map((project) => (
                        <button
                          key={project.id}
                          onClick={() => handleProjectSelection(project)}
                          disabled={!!projectId.get() && !!currentProjectId && project.id !== currentProjectId}
                          className={`p-4 border rounded-lg transition-colors ${!!projectId.get() && !!currentProjectId && project.id !== currentProjectId ? 'opacity-50 cursor-not-allowed' : ''} text-left flex flex-col h-[70px] justify-between relative ${
                            selectedProject?.id === project.id || project.id === currentProjectId
                              ? 'bg-dualite-background-buttonHover border-dualite-border-primary'
                              : 'bg-dualite-background-tertiary border-dualite-border-primary hover:bg-dualite-background-tertiary-hover'
                          }`}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex items-center gap-2">
                              <div className="text-dualite-text-modal-primary font-medium text-sm">{project.name}</div>
                            </div>
                            <div className="flex items-center gap-[6px]">
                              {project.status !== 'ACTIVE_HEALTHY' && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleMakeProjectActive(project);
                                  }}
                                  className="text-xs bg-dualite-background-tertiary rounded-md px-2 py-1 text-dualite-text-modal-tertiary hover:text-dualite-text-modal-primary hover:bg-dualite-background-tertiary-hover transition-colors flex items-center gap-1"
                                >
                                  <div className="i-ph:warning w-3.5 h-3.5 text-red-500" />
                                  <span className="underline text-red-500">{t('modals.supabase.activate')}</span>
                                </button>
                              )}
                              {(selectedProject?.id === project.id || project.id === currentProjectId) &&
                                project.status === 'ACTIVE_HEALTHY' && (
                                  <svg
                                    width="16"
                                    height="16"
                                    viewBox="0 0 16 16"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                  >
                                    <circle cx="8" cy="8" r="7" fill="white" />
                                    <path
                                      d="M11.5 5.5L6.75 10.25L4.5 8"
                                      stroke="black"
                                      strokeWidth="1.5"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                    />
                                  </svg>
                                )}
                            </div>
                          </div>
                          <div className="text-dualite-text-modal-tertiary text-xs">
                            {formatDate(project.created_at)}
                          </div>
                        </button>
                      ))}
                    </div>
                    {currentProjectId && (
                      <div className="flex gap-2 mb-4">
                        <button
                          onClick={() =>
                            window.open(
                              `https://supabase.com/dashboard/project/${currentProjectId}/database/tables`,
                              '_blank',
                            )
                          }
                          disabled={loading || isDisconnecting || !currentProjectId}
                          className="flex items-center gap-1 text-dualite-text-secondary transition-colors px-2 py-1 rounded-full bg-dualite-background-secondary hover:bg-dualite-background-secondary-hover cursor-pointer border border-dualite-border-secondary hover:border-dualite-border-primary disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <span className="text-xs text-dualite-text-secondary">Open Database Tables</span>
                          <div className="i-ph:link w-3 h-3 text-[#8DD290]" />
                        </button>
                        <button
                          onClick={() =>
                            window.open(
                              `https://supabase.com/dashboard/project/${currentProjectId}/settings/general`,
                              '_blank',
                            )
                          }
                          disabled={loading || isDisconnecting || !currentProjectId}
                          className="flex items-center gap-1 text-dualite-text-secondary transition-colors px-2 py-1 rounded-full bg-dualite-background-secondary hover:bg-dualite-background-secondary-hover cursor-pointer border border-dualite-border-secondary disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <span className="text-xs text-dualite-text-secondary">Open Cloud Settings</span>
                          <div className="i-ph:link w-3 h-3 text-[#8DD290]" />
                        </button>
                        <button
                          onClick={() =>
                            window.open(`https://supabase.com/dashboard/project/${currentProjectId}`, '_blank')
                          }
                          disabled={loading || isDisconnecting || !currentProjectId}
                          className="flex items-center gap-1 text-dualite-text-secondary transition-colors px-2 py-1 rounded-full bg-dualite-background-secondary hover:bg-dualite-background-secondary-hover cursor-pointer border border-dualite-border-secondary disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <span className="text-xs text-dualite-text-secondary">Open Cloud Dashboard</span>
                          <div className="i-ph:link w-3 h-3 text-[#8DD290]" />
                        </button>
                      </div>
                    )}
                    <button
                      disabled={
                        !selectedProject ||
                        selectedProject.status !== 'ACTIVE_HEALTHY' ||
                        (!!projectId.get() && !!currentProjectId)
                      }
                      onClick={() => {
                        if (selectedProject) {
                          handleProjectSelect(selectedProject);
                          setStep('credentials');
                        }
                      }}
                      className={`w-full flex items-center justify-center gap-2 px-6 py-4 rounded-2xl transition-all text-base font-medium ${
                        selectedProject &&
                        selectedProject.status === 'ACTIVE_HEALTHY' &&
                        !(projectId.get() && currentProjectId)
                          ? 'bg-dualite-background-cta text-dualite-text-cta hover:bg-dualite-background-cta-hover shadow-lg shadow-white/10'
                          : 'bg-dualite-background-tertiary border border-dualite-border-secondary text-dualite-text-modal-tertiary hover:bg-dualite-background-tertiary-hover'
                      } disabled:opacity-50 disabled:cursor-not-allowed`}
                      title={
                        projectId.get() && currentProjectId
                          ? t('modals.supabase.projectAlreadyConnected')
                          : t('modals.supabase.selectProject')
                      }
                    >
                      <span>
                        {projectId.get() && currentProjectId
                          ? t('modals.supabase.projectAlreadyConnected')
                          : t('modals.supabase.selectProject')}
                      </span>
                    </button>
                  </div>
                ) : (
                  <div className="text-center py-2">
                    <div className="w-16 h-16 bg-white/10 rounded-lg flex items-center justify-center mx-auto mb-6">
                      <svg width="96" height="97" viewBox="0 0 96 97" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <rect y="0.5" width="96" height="96" rx="12" fill="white" fillOpacity="0.2" />
                        <path
                          fillRule="evenodd"
                          clipRule="evenodd"
                          d="M21.5182 29.8557C21.3333 30.8359 21.3333 32.0126 21.3333 34.366V53.1667C21.3333 63.2232 21.3333 68.2518 24.4574 71.3758C27.5817 74.5 32.6099 74.5 42.6666 74.5H53.3333C63.3898 74.5 68.4183 74.5 71.5423 71.3758C74.6666 68.2518 74.6666 63.2232 74.6666 53.1667V47.2944C74.6666 40.2748 74.6666 36.765 72.6146 34.4836C72.426 34.2737 72.2263 34.074 72.0165 33.8852C69.7349 31.8334 66.2253 31.8334 59.2055 31.8334H58.209C55.1325 31.8334 53.5943 31.8334 52.161 31.4248C51.3735 31.2003 50.6143 30.8859 49.8988 30.4878C48.5965 29.7631 47.5087 28.6754 45.3333 26.5L43.8658 25.0327C43.1367 24.3036 42.7722 23.939 42.3891 23.6214C40.7373 22.2521 38.711 21.4128 36.5748 21.213C36.0793 21.1667 35.5637 21.1667 34.5326 21.1667C32.1792 21.1667 31.0025 21.1667 30.0223 21.3516C25.7074 22.1657 22.3323 25.5408 21.5182 29.8557ZM47.9999 45.8334C49.1045 45.8334 49.9999 46.7288 49.9999 47.8334V51.1667H53.3333C54.4378 51.1667 55.3332 52.0622 55.3332 53.1667C55.3332 54.2712 54.4378 55.1667 53.3333 55.1667H49.9999V58.5C49.9999 59.6046 49.1045 60.5 47.9999 60.5C46.8954 60.5 45.9999 59.6046 45.9999 58.5V55.1667H42.6666C41.562 55.1667 40.6666 54.2712 40.6666 53.1667C40.6666 52.0622 41.562 51.1667 42.6666 51.1667H45.9999V47.8334C45.9999 46.7288 46.8954 45.8334 47.9999 45.8334Z"
                          fill="white"
                        />
                      </svg>
                    </div>
                    <h3 className="text-dualite-text-modal-primary font-bold text-xl mb-2">
                      {t('modals.supabase.noProjectsTitle')}
                    </h3>
                    <p className="text-dualite-text-modal-secondary text-sm mb-8">
                      {t('modals.supabase.noProjectsMessage')}
                    </p>
                    <button
                      onClick={() => handleCreateProject('empty_state')}
                      className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-dualite-background-cta text-dualite-text-cta rounded-lg hover:bg-dualite-background-cta-hover transition-all text-sm font-medium"
                    >
                      <span>{t('modals.supabase.createSupabaseProject')}</span>
                    </button>
                  </div>
                )}
              </div>
            )}

            {step === 'credentials' && (
              <div className="flex flex-col items-center justify-center py-8">
                <div className="i-svg-spinners:90-ring-with-bg w-8 h-8 text-dualite-text-modal-secondary" />
                <p className="text-sm text-dualite-text-modal-secondary">
                  {t('modals.supabase.retrievingCredentials')}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
}
